// src/components/pages/UserProfile.js

import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  Container,
  Box,
  Typography,
  CircularProgress,
  Button,
  Tooltip, // Import Tooltip from MUI
} from "@mui/material";
import HelpOutlineIcon from "@mui/icons-material/HelpOutline";
import {
  fetchUserProfile,
  fetchUserProfileByUserName,
  fetchUserChallenges,
  fetchUserSubmissions,
  fetchUserMembershipsFromFirestore, // Ensure this is imported
} from "../../services/profileService";
// import ProfileTabs from "../organisms/Profile/ProfileTabs";
import ProfileHeader from "../organisms/Profile/ProfileHeader";
import ProfileChallengesSection from "../organisms/Profile/ProfileChallengesSection";
import ProfileSubmissionsSection from "../organisms/Profile/ProfileSubmissionsSection";
import { useTheme } from "styled-components";
import CustomCard from "../molecules/ChallengeCreator/CustomCard";
import { useAuth } from "../../context/AuthContext"; // Updated import path
import { formatDate, calculateRenewDate } from "../../utils/dateUtils"; // Import helper functions
import ArtistProfile from "./MarketPlace/ArtistProfile";
import { getArtistProfile } from '../../services/profileService';
import BackHeader from '../pages/MarketPlace/Components/BackHeader';

const UserProfile = () => {
  const { profileId,username } = useParams();
  const navigate = useNavigate();
  const theme = useTheme();
  const { currentUser, loading: authLoading, error: authError } = useAuth();

  // Local state for profile data
  const [loading, setLoading] = useState(true);
  const [userProfile, setUserProfile] = useState(null);
  const [challenges, setChallenges] = useState([]);
  const [submissions, setSubmissions] = useState([]);
  const [memberships, setMemberships] = useState([]); // Local state for memberships
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState(0);
  const [artist, setArtist] = useState({});
  

  useEffect(() => {
    const fetchProfilesAndChallenges = async () => {
      setLoading(true);
      try {
        const userToFetch = profileId || (currentUser && currentUser.uid);
        console.log(`User to Fetch UID: ${userToFetch}`); // Debugging Log

        if (userToFetch) {
          let fetchedProfile;
          console.log("username",username)
          if(username){
            fetchedProfile = await fetchUserProfileByUserName(username);
          }
          else{
          if (profileId) {
            // Fetch another user's profile
            fetchedProfile = await fetchUserProfile(userToFetch);
            console.log(
              `Fetched Profile for UID ${userToFetch}:`,
              fetchedProfile
            ); // Debugging Log
          } else {
            // Fetch current user's profile
            fetchedProfile = await fetchUserProfile(currentUser.uid);
            console.log("Fetched Current User's Profile:", fetchedProfile); // Debugging Log
          }
        }
        console.log("fetchedProfile",fetchedProfile)
          setUserProfile(fetchedProfile);

          // Fetch challenges created by the target user
          const fetchedChallenges = await fetchUserChallenges(
            userToFetch,
            currentUser?.uid // Assuming currentUser.uid is needed
          );
          console.log("Fetched Challenges:", fetchedChallenges); // Debugging Log
          setChallenges(fetchedChallenges.slice(0, 3));

          // Fetch submissions created by the target user
          const fetchedSubmissions = await fetchUserSubmissions(
            userToFetch,
            currentUser?.uid // Assuming currentUser.uid is needed
          );
          console.log("Fetched Submissions:", fetchedSubmissions); // Debugging Log
          // setSubmissions(fetchedSubmissions.slice(0, 9)); // Limit to 9 items
          setSubmissions(fetchedSubmissions.filter(x=>x.challengeId));

          // Determine if viewing own profile
          const isOwnProfile = currentUser ? (!profileId || profileId === currentUser.uid) : false;


          console.log("Rendering memberships:", isOwnProfile, memberships);

          if (isOwnProfile && currentUser) {
            console.log("Fetching memberships for own profile.");
            const fetchedMemberships = await fetchUserMembershipsFromFirestore(
              currentUser.uid
            );
            console.log("Fetched Memberships:", fetchedMemberships);
            setMemberships(fetchedMemberships);
          } else {
            console.log(
              "Not fetching memberships (profileId exists or no currentUser)."
            );
            setMemberships([]); // Clear memberships if viewing others
          }
        } else {
          console.log("No userToFetch found. Redirecting to sign-in.");
          navigate("/challenge/signin");
        }
      } catch (err) {
        console.error("Error fetching profile or challenges data:", err);
        setError("Error fetching profile or challenges data.");
      } finally {
        setLoading(false);
      }
    };

    // Only fetch data if authentication is not loading
    if (!authLoading) {
      fetchProfilesAndChallenges();
    }
    // `username` is included because /challenge/profile/username/:username
    // reuses this same element: moving between two usernames changes the param
    // without remounting, so leaving it out left the previous profile on screen.
    // `memberships` is deliberately excluded — it is only read by the debug log
    // above, and this effect calls setMemberships, so depending on it would loop.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [profileId, username, navigate, currentUser, authLoading]);

  useEffect(() => {
    const loadArtistProfile = async () => {
      try {
        const data = await getArtistProfile(userProfile?.userName || username);
        console.log("artist profile: ", data);
        setArtist(data);
      } catch (error) {
        console.error("Failed to load artist", error);
      }
    };

    if (userProfile?.userName || username) loadArtistProfile();
  }, [userProfile,username]);

  // Unused only because the <ProfileTabs> block in the render is commented out.
  // While it stays commented, activeTab is pinned to 0 and the activeTab === 1
  // branch further down is unreachable. Restore them together.
  // eslint-disable-next-line no-unused-vars
  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
  };

  // Handle global auth loading and error states
  if (authLoading) {
    return (
      <Container maxWidth="lg">
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            height: "80vh", // Adjust height as needed
          }}
        >
          <CircularProgress
            sx={{
              color: theme.colors.red,
            }}
          />
        </Box>
      </Container>
    );
  }

  if (authError) {
    return (
      <Container maxWidth="lg">
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            height: "80vh", // Adjust height as needed
          }}
        >
          <Typography variant="h6" color="error">
            {authError.message || "Authentication Error"}
          </Typography>
        </Box>
      </Container>
    );
  }

  // Handle loading state for profile, challenges, etc.
  if (loading) {
    return (
      <Container maxWidth="lg">
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            height: "80vh", // Adjust height as needed
          }}
        >
          <CircularProgress
            sx={{
              color: theme.colors.red,
            }}
          />
        </Box>
      </Container>
    );
  }

  // Handle error state for profile, challenges, etc.
  if (error) {
    return (
      <Container maxWidth="lg">
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            height: "80vh", // Adjust height as needed
          }}
        >
          <Typography variant="h6" color="error">
            {error}
          </Typography>
        </Box>
      </Container>
    );
  }

  // Handle case where no userProfile is found and no profileId is provided
  if (!userProfile && !profileId && !username) {
    return (
      <Container maxWidth="lg">
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            justifyContent: "center",
            alignItems: "center",
            height: "80vh", // Adjust height as needed
          }}
        >
          <Typography variant="h4" component="h1" gutterBottom>
            You are not signed in
          </Typography>
          <Button
            variant="contained"
            color="primary"
            onClick={() => navigate("/challenge/signin")}
          >
            Sign In
          </Button>
        </Box>
      </Container>
    );
  }

  // Extract first name from userProfile displayName
  const displayName = userProfile?.displayName || "User";
  const firstName = displayName.split(" ")[1] || displayName.split(" ")[0];

  // Determine if viewing own profile for rendering memberships
  const isOwnProfile = currentUser ? (!profileId || profileId === currentUser.uid) : false;
  console.log("Rendering memberships:", isOwnProfile, memberships);

  return (
    <Container
      sx={{
        mt: 4,
        maxWidth: {
          xs: "xs",
          sm: "sm",
          md: "md",
          lg: "lg",
          xl: "xl",
        },
      }}
    >
      {/* DISABLED: while this stays commented out nothing can change activeTab,
          so it is stuck at 0 and the activeTab === 1 block below never renders.
          Restore this together with handleTabChange above. */}
      {/* {!profileId && (
        <ProfileTabs activeTab={activeTab} handleTabChange={handleTabChange} />
      )} */}

      <Box>
        {activeTab === 0 && (
          <Box>
      <BackHeader />

            <ProfileHeader
              userProfile={userProfile}
              artistProfile={artist?.artist}
              currentUser={currentUser}
              theme={theme}
            />
            <Box sx={{ pb: 4 }} />

            {/* Display Memberships if viewing own profile */}
            {!username &&
            isOwnProfile && (
              <CustomCard title="My Memberships">
                {memberships.length > 0 ? (
                  memberships.map((membership) => {
                    // Use helper functions for formatting
                    const formattedMemberSince = formatDate(
                      membership.createdAt
                    );
                    const formattedRenewsOn = calculateRenewDate(
                      membership.createdAt
                    );

                    return (
                      <Box
                        key={membership.id}
                        sx={{
                          display: "flex",
                          flexDirection: "column", // Change to column for better layout
                          mb: 1,
                          pb: 1,
                          borderBottom: `1px solid ${theme.colors.palePurple}`,
                        }}
                      >
                        <Typography
                          variant="body1"
                          sx={{
                            fontFamily: "basic-sans",
                            color: theme.colors.lightGray,
                            mb: 0.5,
                          }}
                        >
                          <strong>Membership:</strong>{" "}
                          {membership.membershipName}
                        </Typography>
                        <Typography
                          variant="body1"
                          sx={{
                            fontFamily: "basic-sans",
                            color: theme.colors.lightGray,
                            mb: 0.5,
                          }}
                        >
                          <strong>Status:</strong> {membership.status}
                        </Typography>
                        <Typography
                          variant="body1"
                          sx={{
                            fontFamily: "basic-sans",
                            color: theme.colors.lightGray,
                            mb: 0.5,
                          }}
                        >
                          <strong>Member since:</strong> {formattedMemberSince}
                        </Typography>
                        {/* Conditionally render "Renews on" if status is active */}
                        {membership.status.toLowerCase() === "active" && (
                          <Typography
                            variant="body1"
                            sx={{
                              fontFamily: "basic-sans",
                              color: theme.colors.lightGray,
                              display: "flex",
                              alignItems: "center", // Vertically center the items
                            }}
                          >
                            <strong>Renews on:&nbsp; </strong>{" "}
                            {formattedRenewsOn}
                            <Tooltip
                              title='1. Log in to your PayPal account. 2. Access your settings by clicking the gear icon in the top-right corner. 3. Navigate to the "Payments" tab. 4. Click on "Manage Automatic Payments." 5. Select "Doopus, LLC" from the list of active merchants. 6. Click "Cancel" to terminate the automatic payment agreement.'
                              arrow
                            >
                              <HelpOutlineIcon
                                fontSize="small"
                                sx={{
                                  ml: 0.5, // Small left margin for spacing
                                  color: theme.colors.lightGray,
                                  cursor: "pointer", // Change cursor to pointer on hover
                                }}
                              />
                            </Tooltip>
                          </Typography>
                        )}
                      </Box>
                    );
                  })
                ) : (
                  <Box
                    sx={{
                      textAlign: "center",
                    }}
                  >
                    <Button
                      variant="contained"
                      onClick={() => navigate("/challenge/memberships/cap")}
                      sx={{
                        backgroundColor: theme.colors.blue,
                        color: theme.colors.white,
                        fontFamily: "basic-sans",
                        fontWeight: "700",
                        textTransform: "none",
                        padding: "10px 20px",
                        borderRadius: "50px",
                        boxShadow: "none",
                        "&:hover": {
                          backgroundColor: theme.colors.green,
                          boxShadow: "none",
                        },
                      }}
                    >
                      Join the Creator's Alliance Program
                    </Button>
                  </Box>
                )}
              </CustomCard>
            )
          }

            {/* Pass profileId to ProfileChallengesSection */}
            {!username &&
              <ProfileChallengesSection
                firstName={firstName}
                profileId={profileId} // Pass the profileId here
                challenges={challenges}
                theme={theme}
              />
            }

            {/* New Submissions Section */}
            {!username &&
              <ProfileSubmissionsSection
                firstName={firstName}
                profileId={profileId}
                submissions={submissions}
                theme={theme}
              />
          }
            {artist &&
              <ArtistProfile artist={artist} userProfile={userProfile} profileId={profileId} />
            }
          </Box>
        )}

        {activeTab === 1 && (
          <Box>
            <Typography variant="h3" component="h1" gutterBottom>
              My Purchases
            </Typography>
            <Typography variant="body1" align="center">
              Display user's purchase information here.
            </Typography>
          </Box>
        )}
      </Box>
    </Container>
  );
};

export default UserProfile;
